import {
    translateParts,
    translatePartsOptional,
} from 'aos-helpers/src/helpers/translations/Translations'
import { MaintenanceJobAssignmentPayload } from 'aos-services/src/services/tasks/types/payload/MaintenanceTaskFormPayload'
import { TaskFormPayload } from 'aos-services/src/services/tasks/types/payload/TaskFormPayload'
import { BrushesBrand } from 'aos-services/src/services/tasks/types/task/BrushesBrand'
import {
    BRUSHES_VEHICLE_OTHER_LABEL,
    BrushesVehicle,
} from 'aos-services/src/services/tasks/types/task/BrushesVehicle'
import {
    allMaintenanceTaskTypes,
    MaintenanceJob,
    MaintenanceTaskType,
} from 'aos-services/src/services/tasks/types/task/MaintenanceTask'
import { TaskMetadata } from 'aos-services/src/services/tasks/types/TaskMetadata'
import {
    Dropdown,
    HStack,
    Icon,
    Input,
    InputField,
    NumberInput,
    Text,
    VStack,
} from 'aos-ui-universal'
import { isNull } from 'lodash'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { maintenanceTaskTypeIcons } from '../../../../utils'
import { TaskFormField } from '../../TaskFormField'
import { vehicleChangePayload } from './vehicleChangePayload'

interface MaintenanceJobAssignmentProps {
    assignment?: MaintenanceJobAssignmentPayload
    maintenanceJobs: MaintenanceJob[]
    metadata: TaskMetadata
    onChange(v: MaintenanceJobAssignmentPayload | undefined): void
    isTaskTemplate: boolean
}

export const MaintenanceJobAssignmentRenderer: FC<MaintenanceJobAssignmentProps> = ({
    assignment,
    maintenanceJobs,
    metadata,
    onChange,
    isTaskTemplate,
}) => {
    const { t: translate } = useTranslation()
    const [maintenanceTaskType, setMaintenanceTaskType] = useState<MaintenanceTaskType>(
        assignment?.job?.jobType ?? MaintenanceTaskType.General,
    )
    const jobs = maintenanceJobs.filter(j => j.jobType === maintenanceTaskType)
    const brushesJob = maintenanceJobs.find(j => j.jobType === MaintenanceTaskType.Brushes)

    const clearJobState = (jobType: MaintenanceTaskType) => {
        if (jobType !== maintenanceTaskType) {
            setMaintenanceTaskType(jobType)
            onChange({ ...assignment, job: undefined })
        }
    }

    useEffect(() => {
        queueMicrotask(() => {
            if (assignment?.job?.jobType) {
                setMaintenanceTaskType(assignment.job.jobType)
            }
        })
    }, [assignment?.job])

    return (
        <VStack className='mr-4 flex-1'>
            <HStack className='flex-1 items-start gap-5'>
                <MaintenanceTaskTypeSelectorRenderer
                    onChange={clearJobState}
                    maintenanceTaskType={maintenanceTaskType}
                />
                <VStack className='flex-1'>
                    {maintenanceTaskType !== MaintenanceTaskType.Brushes && (
                        <Dropdown<MaintenanceJob>
                            value={assignment?.job ?? null}
                            items={jobs}
                            labelRenderer={v => v.label}
                            placeholder={translate('form.maintenance.assignments.job.placeholder', {
                                ns: 'tasks',
                            })}
                            emptyText={translate('dropdown.empty', { ns: 'common' })}
                            onChange={job =>
                                onChange({
                                    ...assignment,
                                    job: isNull(job) ? undefined : job,
                                    brushesVehicle: undefined,
                                    brushesBrand: undefined,
                                    engineLastShiftHours: undefined,
                                    brushesLastShiftHours: undefined,
                                    totalHours: undefined,
                                })
                            }
                        />
                    )}
                </VStack>
            </HStack>
            {maintenanceTaskType === MaintenanceTaskType.Brushes && (
                <HStack className='flex-1'>
                    <MaintenanceJobBrushesReplacementRenderer
                        assignment={assignment}
                        metadata={metadata}
                        isTaskTemplate={isTaskTemplate}
                        onChange={brushesAssignment => {
                            onChange({ ...assignment, ...brushesAssignment, job: brushesJob })
                        }}
                    />
                </HStack>
            )}
        </VStack>
    )
}

interface MaintenanceTaskTypeSelectorRendererProps {
    maintenanceTaskType: MaintenanceTaskType
    onChange(v: MaintenanceTaskType): void
}

export const MaintenanceTaskTypeSelectorRenderer: FC<MaintenanceTaskTypeSelectorRendererProps> = ({
    maintenanceTaskType,
    onChange,
}) => (
    <Dropdown<MaintenanceTaskType>
        value={maintenanceTaskType}
        items={allMaintenanceTaskTypes}
        labelRenderer={v => v}
        hasClear={false}
        searchable={false}
        valueRenderer={v => (
            <Icon
                as={maintenanceTaskTypeIcons[v]}
                size={24}
                className='shrink-0 text-aos-text-muted'
            />
        )}
        itemRenderer={v => (
            <HStack className='items-center gap-3'>
                <Icon
                    as={maintenanceTaskTypeIcons[v]}
                    size={24}
                    className='shrink-0 text-aos-text-default'
                />
                <Text size='body' tone='muted'>
                    {v}
                </Text>
            </HStack>
        )}
        onChange={v => v && onChange(v)}
    />
)

interface MaintenanceJobBrushesReplacementRendererProps {
    assignment?: MaintenanceJobAssignmentPayload
    metadata: TaskMetadata
    onChange(v: Partial<MaintenanceJobAssignmentPayload> | Partial<TaskFormPayload>): void
    isTaskTemplate: boolean
}

const BRUSHES_KEY_PREFIX = 'tasks.form.maintenance.assignments'

export const MaintenanceJobBrushesReplacementRenderer: FC<
    MaintenanceJobBrushesReplacementRendererProps
> = ({ assignment, metadata, onChange, isTaskTemplate }) => {
    const { t: translate } = useTranslation()
    const label = (field: string) => translateParts(translate)(BRUSHES_KEY_PREFIX, field, 'label')
    const placeholder = (field: string) =>
        translatePartsOptional(translate)(BRUSHES_KEY_PREFIX, field, 'placeholder')

    const isDuplicateVehicleName = useMemo(() => {
        if (!assignment?.newBrushVehicle?.trim()) {
            return false
        }
        const name = assignment.newBrushVehicle.trim().toLowerCase()
        return metadata.brushesVehicles.some(v => v.label.trim().toLowerCase() === name)
    }, [assignment, metadata])

    const hoursField = (
        field: string,
        value: number | undefined,
        onChangeHours: (v: number | undefined) => void,
    ) => (
        <TaskFormField
            label={label(field)}
            required={!isTaskTemplate}
            seleniumLocation={field}
            className='mb-3'
        >
            <NumberInput placeholder={placeholder(field)} value={value} onChange={onChangeHours} />
        </TaskFormField>
    )

    return (
        <VStack className='flex-1 pt-3'>
            <HStack className='items-start gap-5'>
                <VStack className='flex-1'>
                    <TaskFormField
                        label={label('brushes-vehicle')}
                        required={!isTaskTemplate}
                        seleniumLocation='brushes-vehicle'
                        className='mb-3'
                    >
                        <Dropdown<BrushesVehicle>
                            items={metadata.brushesVehicles}
                            value={assignment?.brushesVehicle ?? null}
                            labelRenderer={(v: BrushesVehicle) => v.label}
                            placeholder={placeholder('brushes-vehicle')}
                            onChange={brushesVehicle =>
                                onChange(vehicleChangePayload(brushesVehicle))
                            }
                        />
                    </TaskFormField>
                </VStack>
                <VStack className='flex-1'>
                    {assignment?.brushesVehicle?.label === BRUSHES_VEHICLE_OTHER_LABEL && (
                        <TaskFormField
                            label={label('new-brushes-vehicle')}
                            seleniumLocation='new-brushes-vehicle'
                            error={
                                isDuplicateVehicleName
                                    ? translate(
                                          'form.maintenance.assignments.new-brushes-vehicle.duplique-error',
                                          { ns: 'tasks' },
                                      )
                                    : undefined
                            }
                            className='mb-3'
                        >
                            <Input isInvalid={isDuplicateVehicleName}>
                                <InputField
                                    placeholder={placeholder('new-brushes-vehicle')}
                                    value={assignment?.newBrushVehicle ?? ''}
                                    onChangeText={newBrushVehicle => onChange({ newBrushVehicle })}
                                />
                            </Input>
                        </TaskFormField>
                    )}
                </VStack>
            </HStack>
            <HStack className='items-start gap-5'>
                <VStack className='flex-1'>
                    <TaskFormField
                        label={label('brushes-brand')}
                        required={!isTaskTemplate}
                        seleniumLocation='brushbrand'
                        className='mb-3'
                    >
                        <Dropdown<BrushesBrand>
                            items={metadata.brushesBrands}
                            value={assignment?.brushesBrand ?? null}
                            labelRenderer={(v: BrushesBrand) => v.name}
                            placeholder={placeholder('brushes-brand')}
                            onChange={brushesBrand =>
                                onChange({ brushesBrand: brushesBrand ?? undefined })
                            }
                        />
                    </TaskFormField>
                </VStack>
                <VStack className='flex-1'>
                    {hoursField(
                        'engine-last-shift-hours',
                        assignment?.engineLastShiftHours,
                        engineLastShiftHours => onChange({ engineLastShiftHours }),
                    )}
                </VStack>
            </HStack>
            <HStack className='items-start gap-5'>
                <VStack className='flex-1'>
                    {hoursField(
                        'brushes-last-shift-hours',
                        assignment?.brushesLastShiftHours,
                        brushesLastShiftHours => onChange({ brushesLastShiftHours }),
                    )}
                </VStack>
                <VStack className='flex-1'>
                    {hoursField('total-hours', assignment?.totalHours, totalHours =>
                        onChange({ totalHours }),
                    )}
                </VStack>
            </HStack>
        </VStack>
    )
}
